import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createEmptySnapshot,
  normalizeDraftSnapshot,
  type ComposerDraftSnapshot,
} from './snapshot';
import {
  documentFromSnapshot,
  snapshotFromDocument,
  type CompositionDocument,
  type CompositionPublication,
} from './document';
import { getCompositionHistory, listCompositions, saveComposition } from './storage';
import { syncCompositions, type CloudStatus, type CompositionApi } from './sync';

const LEGACY_KEY = 'museflow_repertoire_create_draft_v1';
const fingerprint = (snapshot: ComposerDraftSnapshot) =>
  JSON.stringify(normalizeDraftSnapshot(snapshot));
const MIGRATED_KEY = `${LEGACY_KEY}:migrated`;
export type LocalSaveStatus = 'loading' | 'saving' | 'saved' | 'error';

export function useCompositionLibrary(
  ownerId: string,
  snapshot: ComposerDraftSnapshot,
  apply: (snapshot: ComposerDraftSnapshot) => void,
  api?: CompositionApi,
) {
  const [documents, setDocuments] = useState<CompositionDocument[]>([]);
  const [status, setStatus] = useState<LocalSaveStatus>('loading');
  const [error, setError] = useState('');
  const [cloudStatus, setCloudStatus] = useState<CloudStatus>('local');
  const [cloudError, setCloudError] = useState('');
  const syncBusy = useRef(false);
  const [ready, setReady] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [restoreSequence, setRestoreSequence] = useState(0);
  const active = useRef<CompositionDocument | null>(null);
  const latest = useRef(snapshot);
  latest.current = snapshot;
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const generation = useRef(0);
  const pendingRestore = useRef<string | null>(null);
  const savedSignature = useRef('');
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const signature = fingerprint(snapshot);

  const fail = useCallback((reason: unknown) => {
    setStatus('error');
    setError(
      reason instanceof Error
        ? reason.message
        : 'Unable to save on this device. Export a composition backup, then retry.',
    );
  }, []);

  const rememberActive = useCallback(
    (document: CompositionDocument) => {
      // A convenience pointer; document durability is acknowledged by IndexedDB.
      try {
        localStorage.setItem(`museflow:composition:active:${ownerId}`, document.id);
      } catch {
        /* use newest document next time */
      }
    },
    [ownerId],
  );

  const load = useCallback(
    (document: CompositionDocument) => {
      active.current = document;
      const restored = snapshotFromDocument(document);
      // Never resume recording, metronome or sound after reopening a sketch.
      restored.builderMode = 'edit';
      restored.metronomeEnabled = false;
      pendingRestore.current = fingerprint(restored);
      savedSignature.current = pendingRestore.current;
      setReady(false);
      setRestoreSequence((sequence) => sequence + 1);
      applyRef.current(restored);
      rememberActive(document);
    },
    [rememberActive],
  );

  useEffect(() => {
    const ticket = ++generation.current;
    active.current = null;
    setReady(false);
    setStatus('loading');
    setCloudStatus('local');
    setCloudError('');
    void (async () => {
      try {
        let found = await listCompositions(ownerId);
        if (!found.length && api && navigator.onLine) {
          try {
            await syncCompositions(ownerId, api, () => ticket !== generation.current);
            found = await listCompositions(ownerId);
          } catch {
            // Starting a local sketch remains available when cloud storage is unreachable.
          }
        }
        if (ticket !== generation.current) return;
        let id: string | null = null;
        try {
          id = localStorage.getItem(`museflow:composition:active:${ownerId}`);
        } catch {
          /* use most recent */
        }
        const legacy = localStorage.getItem(LEGACY_KEY);
        if (legacy && !localStorage.getItem(MIGRATED_KEY)) {
          const normalized = normalizeDraftSnapshot(JSON.parse(legacy));
          if (!normalized)
            throw new Error(
              'The previous draft could not be read. Its original data has been kept.',
            );
          // Cloud sketches must not hide the previous device-local draft.
          const migrated = await saveComposition(documentFromSnapshot(normalized, ownerId));
          localStorage.setItem(MIGRATED_KEY, JSON.stringify({ ownerId, id: migrated.id }));
          found.unshift(migrated);
          id = migrated.id;
        }
        let document = found.find((item) => item.id === id) ?? found[0];
        if (!document) {
          document = await saveComposition(documentFromSnapshot(createEmptySnapshot(), ownerId));
          found.unshift(document);
        }
        if (ticket !== generation.current) return;
        setDocuments(found);
        load(document);
      } catch (reason) {
        if (ticket === generation.current) fail(reason);
      }
    })();
    return () => {
      generation.current += 1;
    };
  }, [ownerId, fail, load, loadAttempt, api]);

  useEffect(() => {
    if (pendingRestore.current === signature) {
      pendingRestore.current = null;
      setReady(true);
      setStatus('saved');
    }
  }, [signature, restoreSequence]);

  const flush = useCallback(async () => {
    const current = active.current;
    if (!current || pendingRestore.current) return;
    const ticket = generation.current;
    const toSave = latest.current;
    const toSaveSignature = fingerprint(toSave);
    if (toSaveSignature === savedSignature.current) return;
    setStatus('saving');
    const operation = queue.current
      .catch(() => undefined)
      .then(async () => {
        if (ticket !== generation.current || active.current?.id !== current.id) return;
        if (toSaveSignature === savedSignature.current) return;
        const saved = await saveComposition(documentFromSnapshot(toSave, ownerId, active.current));
        if (ticket !== generation.current) return;
        active.current = saved;
        savedSignature.current = toSaveSignature;
        rememberActive(saved);
        setDocuments((items) => [saved, ...items.filter((item) => item.id !== saved.id)]);
        if (saved.id !== current.id) {
          setError('Another tab changed this sketch. Your edits were saved as a conflict copy.');
          applyRef.current({ ...latest.current, draftTitle: saved.title });
        } else {
          setError('');
        }
        if (fingerprint(latest.current) === toSaveSignature) setStatus('saved');
      });
    queue.current = operation;
    try {
      await operation;
    } catch (reason) {
      if (ticket === generation.current) fail(reason);
      throw reason;
    }
  }, [ownerId, fail, rememberActive]);

  useEffect(() => {
    if (!ready) return;
    if (signature === savedSignature.current) return;
    setStatus('saving');
    const timeout = window.setTimeout(() => {
      void flush().catch(() => undefined);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [ready, signature, flush]);

  useEffect(() => {
    const handleHidden = () => {
      if (document.visibilityState === 'hidden') void flush().catch(() => undefined);
    };
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (active.current && fingerprint(latest.current) !== savedSignature.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    document.addEventListener('visibilitychange', handleHidden);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleHidden);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      void flush().catch(() => undefined);
    };
  }, [flush]);

  const open = useCallback(
    async (document: CompositionDocument) => {
      try {
        await flush();
        const current = (await listCompositions(ownerId)).find((item) => item.id === document.id);
        if (!current) throw new Error('This sketch is no longer available on this device.');
        load(current);
        return true;
      } catch (reason) {
        fail(reason);
        return false;
      }
    },
    [flush, load, ownerId, fail],
  );

  const create = useCallback(
    async (initial = createEmptySnapshot()) => {
      try {
        await flush();
        const document = await saveComposition(documentFromSnapshot(initial, ownerId));
        setDocuments((items) => [document, ...items]);
        load(document);
        return true;
      } catch (reason) {
        fail(reason);
        return false;
      }
    },
    [ownerId, flush, load, fail],
  );

  const syncNow = useCallback(async () => {
    if (!api || !ready || syncBusy.current) return;
    if (!navigator.onLine) {
      setCloudStatus('offline');
      return;
    }
    // A take is saved locally while recording; upload a completed observation archive.
    if (latest.current.performanceTakes?.some((take) => take.status === 'recording')) return;
    const ticket = generation.current;
    syncBusy.current = true;
    setCloudStatus('syncing');
    try {
      await flush();
      if (ticket !== generation.current) return;
      const result = await syncCompositions(ownerId, api, () => ticket !== generation.current);
      if (ticket !== generation.current) return;
      const refreshed = await listCompositions(ownerId);
      if (ticket !== generation.current) return;
      setDocuments(refreshed);
      const conflict = result.conflicts.find((item) => item.id === active.current?.id);
      const current = refreshed.find(
        (item) => item.id === (conflict?.copyId ?? active.current?.id),
      );
      if (current && active.current) {
        if (conflict) {
          // Subsequent local edits continue on the conflict copy.
          active.current = current;
          applyRef.current({ ...latest.current, draftTitle: current.title });
          rememberActive(current);
        } else if (current.revision === active.current.revision) {
          active.current = current; // cloud acknowledgment only; keep playing and selecting
        } else if (
          !pendingRestore.current &&
          fingerprint(latest.current) === savedSignature.current
        ) {
          load(current);
        }
        // If unsaved edits arrived during download, retain the old expected revision.
        // The local revision check will preserve them as a copy on the next flush.
      }
      setCloudError(
        result.conflicts.length
          ? 'Another device changed this composition. Both versions are in Sketches; your edits are in a conflict copy.'
          : '',
      );
      setCloudStatus(result.pending ? 'local' : 'synced');
    } catch (reason) {
      if (ticket === generation.current) {
        setCloudStatus(navigator.onLine ? 'error' : 'offline');
        setCloudError(
          reason instanceof Error
            ? reason.message
            : 'Cloud sync was interrupted. Your local sketches are safe.',
        );
      }
    } finally {
      syncBusy.current = false;
    }
  }, [api, ready, flush, ownerId, load, rememberActive]);

  useEffect(() => {
    if (!api || !ready) return;
    const timer = window.setTimeout(() => {
      void syncNow();
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [api, ready, signature, restoreSequence, syncNow]);

  useEffect(() => {
    if (!api) return;
    const online = () => {
      void syncNow();
    };
    const offline = () => setCloudStatus('offline');
    const interval = window.setInterval(online, 30000);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    window.addEventListener('focus', online);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      window.removeEventListener('focus', online);
    };
  }, [api, syncNow]);

  return {
    documents,
    status,
    error,
    ready,
    cloudStatus:
      cloudStatus === 'synced' &&
      active.current?.cloud?.syncedLocalRevision !== active.current?.revision
        ? ('local' as const)
        : cloudStatus,
    cloudError,
    syncNow,
    cloudEnabled: !!api,
    history: async () => {
      await flush();
      const current = active.current;
      if (!current) return [];
      if (api && current.cloud && navigator.onLine) {
        const versions = await api.history(current.id);
        versions.forEach((version) => {
          if (version.id !== current.id || version.ownerId !== ownerId)
            throw new Error('Unexpected composition history response.');
          snapshotFromDocument(version);
        });
        return versions;
      }
      return getCompositionHistory(ownerId, current.id);
    },
    activeId: active.current?.id,
    activeDocument: active.current,
    /**
     * Records the outcome of a publish attempt on the composition itself, so the
     * link to the repertoire item survives a reload and a retry can reuse its
     * operation instead of creating a duplicate.
     */
    setPublication: async (publication: CompositionPublication) => {
      const current = active.current;
      if (!current) return undefined;
      const saved = await saveComposition({ ...current, publication });
      active.current = saved;
      rememberActive(saved);
      setDocuments((items) => [saved, ...items.filter((item) => item.id !== saved.id)]);
      return saved;
    },
    open,
    create,
    flush,
    retry: () => {
      if (!active.current) setLoadAttempt((attempt) => attempt + 1);
      else void flush().catch(() => undefined);
    },
    fail,
  };
}
